import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What are Seedoza Energy Bites?',
      answer: 'Seedoza Energy Bites are healthy snack balls made from a blend of five super seeds (pumpkin, sunflower, sesame, flax, and dates), naturally sweetened with dates and coated with dark chocolate. They\'re designed to provide natural, sustained energy.'
    },
    {
      question: 'Are Seedoza Energy Bites suitable for vegetarians/vegans?',
      answer: 'Yes! All our products are 100% plant-based and suitable for both vegetarians and vegans. We use only natural, wholesome ingredients from plants.'
    },
    {
      question: 'Do your products contain refined sugar?',
      answer: 'No, we never use refined sugar. Our Energy Bites are naturally sweetened using dates, which provide natural sweetness along with fiber and essential minerals.'
    },
    {
      question: 'What are the available pack sizes?',
      answer: 'We offer three convenient pack sizes: 100g (perfect for trying), 250g (great for regular snacking), and 500g (best value for families or regular consumers).'
    },
    {
      question: 'How should I store the Energy Bites?',
      answer: 'Store in a cool, dry place away from direct sunlight. Once opened, seal the pack properly and consume within 30 days for best freshness. Refrigeration is not necessary but can extend shelf life.'
    },
    {
      question: 'Are there any allergens in your products?',
      answer: 'Our products contain seeds (pumpkin, sunflower, sesame, flax) and may contain traces of nuts. If you have specific allergies, please check the ingredient list carefully or contact us.'
    },
    {
      question: 'How many Energy Bites should I eat per day?',
      answer: 'We recommend 2-3 Energy Bites per day as a healthy snack. Each bite is approximately 15-20g. However, you can adjust based on your dietary needs and activity level.'
    },
    {
      question: 'Do you ship across India?',
      answer: 'Yes, we ship to all major cities and towns across India. Delivery typically takes 3-7 business days depending on your location.'
    },
    {
      question: 'What is your return policy?',
      answer: 'We want you to be completely satisfied. If you receive a damaged product or are unhappy with your order, please contact us within 7 days of delivery for a replacement or refund.'
    },
    {
      question: 'Are your products certified organic?',
      answer: 'While we use high-quality, natural ingredients, we are currently working towards organic certification. All our ingredients are carefully sourced from trusted suppliers.'
    },
    {
      question: 'Can I give Seedoza Energy Bites to children?',
      answer: 'Yes! Our Energy Bites are made with natural ingredients and are suitable for children above 3 years. They make a great alternative to sugary snacks. However, please supervise young children while eating.'
    },
    {
      question: 'Do you offer bulk orders or corporate gifting?',
      answer: 'Yes, we do! For bulk orders, corporate gifting, or custom requirements, please contact us at hello@seedoza.com with your requirements, and we\'ll be happy to create a custom package for you.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div data-testid="faq-page">
      {/* Hero Section */}
      <section className="section-spacing bg-gradient-to-b from-background to-muted" data-testid="faq-hero">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">Frequently Asked Questions</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Find answers to common questions about Seedoza Energy Bites, ingredients, ordering, and more.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-spacing">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-card overflow-hidden transition-all"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-muted/20 transition-colors"
                  data-testid={`faq-question-${index}`}
                >
                  <h3 className="text-lg md:text-xl font-heading font-semibold pr-4">
                    {faq.question}
                  </h3>
                  <ChevronDown
                    className={`w-6 h-6 text-primary flex-shrink-0 transition-transform ${
                      openIndex === index ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-6" data-testid={`faq-answer-${index}`}>
                    <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section-spacing bg-primary text-primary-foreground">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Still Have Questions?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            We're here to help! Reach out to our team and we'll get back to you as soon as possible.
          </p>
          <a href="/contact">
            <button className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-4 font-semibold shadow-lg transition-colors" data-testid="faq-contact-button">
              Contact Us
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
